<template>
	<template v-if="visible">
	<span v-if="inputType === 'password'" class="inputs-row fd-pasword"><input :type="passMask ? 'password':'text'" v-model="value" :class="[clazz, errorClass, 'input-password']" :style="myStyle" :readonly="ro" v-bind="attrs"/><icon-button
		:name="passMask ? 'eye-cross' : 'eye'" class="pass-show" @click="toggleMask"></icon-button></span>
	<span v-else-if="input.itype === 'place'" class="inputs-row fd-place"><input type="text" v-model="value" :class="[clazz, errorClass, 'input-place']" :style="myStyle" :readonly="ro" v-bind="attrs"/><a v-if="locationType !== 'empty'" :href="locationLink" target="_blank" class="loc-link"><sym-icon
		 :name="locationType === 'url' ? 'online-meet':'location'"></sym-icon></a></span>
	<span v-else-if="input.itype === 'url'" class="inputs-row fd-place"><input type="url" v-model="value" :class="[clazz, errorClass, 'input-place']" :style="myStyle" :readonly="ro" v-bind="attrs"/><a v-if="!!value" :href="value" target="_blank" class="loc-link"><sym-icon
		name="web"/></a></span>
	<input ref="inputEl" v-else :type="inputType" v-model="value" :class="[clazz, errorClass, 'input-'+inputType]" :style="myStyle" :readonly="ro" v-bind="attrs"/>
	</template>
</template>

<script setup lang="ts">
import useEditor, {type EditorProps, editorProps} from "./Editor";
import {computed, ref} from "vue";
import IconButton from "../helpers/IconButton.vue";
import type {Input} from "@t/fd/editor/Input";
import resolve, {EAMeta} from "@fd/editors/eaddress/EAResolver.ts";
import {EaType} from "@t/customer/EAddress.ts";
import SymIcon from "@fd/helpers/SymIcon.vue";
import {injectEnv} from "@fd/Environment.ts";

const uprops = defineProps({...editorProps});
const props = uprops as EditorProps;
const env = injectEnv();

const input = computed<Input>(() => props.part as Input);

const {visible, value, clazz, style, ro, errorClass, errorMessage, tabindexBinding} = useEditor(props);

const passMask = ref<boolean>(true);
const inputEl = ref<HTMLSpanElement | null>(null);

function toggleMask() {
	passMask.value = !passMask.value;
}

const locationLink = computed<string>(() => {
	if (locationType.value === "url") return locationMetaInfo.value.href;
	else if (locationType.value === "text") return "https://maps.google.com/?q=" + encodeURIComponent(value.value);
	return "#";
});

const inputType = computed(() => {
	if (input.value.itype) {
		//if (input.value.itype === "place") return "text";
		return input.value.itype;
	} else {
		if ("integer" === input.value.cast) return "number";
		return "text";
	}
});

type LocationType = "empty" | "url" | "text";

const locationMetaInfo = computed<EAMeta>(() => {
	return resolve(value.value);
});

const locationType = computed<LocationType>(() => {
	const meta:EAMeta = locationMetaInfo.value;
	if (meta.empty) return "empty";
	else if (meta.siteUrl) return "url";
	else return "text";
})

const myStyle = computed<any>(() => {
	const s = [style.value];
	if (typeof input.value.size === "number") {
		s.push("max-width: calc("+input.value.size+" * 1ch + 2 * var(--input-h-padding))");
	}
	if (locationType.value === "url") s.push({ textDecoration: "underline" });
	return s;
});

const attrs = computed(() => {
	const a: any = tabindexBinding();
	if (input.value.iname) a.name = input.value.iname;
	if (typeof input.value.placeholder === "string") a.placeholder = input.value.placeholder;
	else if (input.value.itype === "password") {
		a.placeholder = passMask.value ? env.msg('password.protected') : env.msg('password.visible');
	}
	if (typeof input.value.maxLength === "number") {
		if (inputType.value==="number" && input.value.maxLength<7) {
			a.max=Math.pow(10,input.value.maxLength)-1
		}
		else {
			a.maxlength = input.value.maxLength;
		}
	}
	//if (typeof input.value.size === "number") a.size = input.value.size;
	if (typeof input.value.autocomplete === "string") a.autocomplete = input.value.autocomplete;
	const errMsg = errorMessage.value;
	if (errMsg) a.title = errMsg;	// if error put as title
	return a;
});

function focus() {
  if (inputEl.value) {
    inputEl.value.focus();
  }
}

defineExpose({ focus });

</script>

<style>
.fi.pass-show {
	line-height: var(--input-height);
}

.pass-show,
.loc-link > .fi {
	margin-right: var(--input-h-padding);
}

/** These input types will be flexy (will grow inside flex container, e.g. .app-field) */
.input-text,
.input-email,
.input-file,
.input-range,
.input-search,
.input-url,
.input-password,
.fd-pasword,
.fd-place {
	flex-grow: 1;
}
.fd-place {
	align-items: baseline;
}
</style>
